import { useState } from 'react';
import { supabase } from '../../../../lib/supabase';
import { logAudit } from '../../../../lib/audit';
import type { AdminRecord } from '../types';
import { ADMIN_ROLES, roleLabel, roleColor } from '../types';

interface Props {
  admin: AdminRecord;
  isSelf: boolean;
  onUpdate: () => void;
  onRemoved: () => void;
}

const permissionDescriptions: Record<string, string[]> = {
  master_admin: [
    'Acesso total ao painel',
    'Gestão de administradores',
    'Configurações do sistema',
    'Auditoria e logs',
    'Gateways e integrações',
    'Planos e assinaturas',
    'Comunicados e usuários',
  ],
  admin: [
    'Gestão de usuários e clientes',
    'Planos e assinaturas',
    'Comunicados',
    'Feedbacks e sugestões',
    'Webhooks',
    'Auditoria (leitura)',
  ],
  support: [
    'Visualização de usuários',
    'Assinaturas (leitura)',
    'Feedbacks e sugestões',
    'Comunicados (leitura)',
  ],
  finance: [
    'Pagamentos e faturamento',
    'Gateways de pagamento',
    'Assinaturas e receita',
  ],
};

export default function AdminAccessSection({ admin, isSelf, onUpdate, onRemoved }: Props) {
  const [currentRole, setCurrentRole] = useState<(typeof ADMIN_ROLES)[number]>(admin.role);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing, setRemoving] = useState(false);

  const roleChanged = currentRole !== admin.role;
  const perms = permissionDescriptions[currentRole] || [];

  const saveRole = async () => {
    setSaving(true);
    setMsg('');
    const { error } = await supabase.from('user_roles')
      .update({ role: currentRole, updated_at: new Date().toISOString() })
      .eq('id', admin.roleId);
    if (!error) {
      await logAudit({ action: 'role_change', resource: 'admin', resource_id: admin.userId, details: { old_role: admin.role, new_role: currentRole } });
      setMsg('Papel atualizado!');
      await onUpdate();
    } else { setMsg('Erro: ' + error.message); }
    setSaving(false);
    setTimeout(() => setMsg(''), 3000);
  };

  const toggleStatus = async () => {
    setSaving(true);
    const newStatus = admin.status === 'active' ? 'inactive' : 'active';
    await supabase.from('user_roles').update({ status: newStatus, updated_at: new Date().toISOString() }).eq('id', admin.roleId);
    await logAudit({ action: newStatus === 'active' ? 'activate' : 'deactivate', resource: 'admin', resource_id: admin.userId, details: { role: admin.role } });
    setSaving(false);
    await onUpdate();
  };

  const removeAdmin = async () => {
    setRemoving(true);
    await supabase.from('user_roles').delete().eq('id', admin.roleId);
    await logAudit({ action: 'delete', resource: 'admin', resource_id: admin.userId, details: { role: admin.role } });
    setRemoving(false);
    onRemoved();
  };

  return (
    <div className="space-y-5">
      {/* Papel e permissões */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
        <div className="flex items-center gap-2.5 mb-5">
          <div className="w-8 h-8 flex items-center justify-center bg-indigo-600/20 rounded-xl">
            <i className="ri-key-2-line text-indigo-400 text-[15px]" />
          </div>
          <h3 className="text-[15px] font-600 text-white">Papel e Permissões</h3>
        </div>

        <div className="grid grid-cols-2 gap-5">
          {/* Role selector */}
          <div>
            <label className="block text-[11px] text-white/40 mb-2 font-500 uppercase tracking-wider">Papel administrativo</label>
            {isSelf ? (
              <div className="p-3 bg-violet-600/10 border border-violet-500/20 rounded-xl">
                <span className={`text-[13px] font-600 ${roleColor[admin.role]?.split(' ')[1] || 'text-white'}`}>
                  <i className="ri-shield-star-line mr-2" />{roleLabel[admin.role]}
                </span>
                <p className="text-[11px] text-violet-300/60 mt-1">Você não pode alterar seu próprio papel.</p>
              </div>
            ) : (
              <>
                <select
                  value={currentRole}
                  onChange={(e) => { setCurrentRole(e.target.value as any); setMsg(''); }}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-violet-500"
                >
                  {ADMIN_ROLES.map((r) => (
                    <option key={r} value={r} className="bg-[#1A1A2E]">{roleLabel[r]}</option>
                  ))}
                </select>
                {roleChanged && (
                  <div className="mt-3 flex items-center gap-3">
                    <button
                      onClick={() => setCurrentRole(admin.role)}
                      className="px-3 py-1.5 border border-white/10 text-white/50 text-[12px] rounded-xl hover:bg-white/5 cursor-pointer whitespace-nowrap transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={saveRole}
                      disabled={saving}
                      className="px-4 py-1.5 bg-violet-600 hover:bg-violet-700 text-white text-[12px] font-600 rounded-xl cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50"
                    >
                      {saving ? 'Salvando...' : `Salvar — ${roleLabel[currentRole]}`}
                    </button>
                  </div>
                )}
                {msg && (
                  <p className={`text-[11px] mt-2 ${msg.includes('Erro') ? 'text-red-400' : 'text-emerald-400'}`}>{msg}</p>
                )}
              </>
            )}
          </div>

          {/* Permission list */}
          <div>
            <p className="text-[11px] text-white/40 mb-2 font-500 uppercase tracking-wider">Permissões do papel</p>
            <ul className="space-y-1.5">
              {perms.map((p) => (
                <li key={p} className="flex items-center gap-2 text-[12px] text-white/60">
                  <i className="ri-check-line text-emerald-400 text-[13px] shrink-0" />{p}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Status + ações */}
      {!isSelf && (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center gap-2.5 mb-5">
            <div className="w-8 h-8 flex items-center justify-center bg-orange-600/20 rounded-xl">
              <i className="ri-shield-keyhole-line text-orange-400 text-[15px]" />
            </div>
            <h3 className="text-[15px] font-600 text-white">Controle de Acesso</h3>
          </div>

          <div className="flex items-center justify-between p-4 bg-white/3 border border-white/8 rounded-xl mb-3">
            <div>
              <p className="text-[13px] text-white font-500">Status do acesso</p>
              <p className="text-[11px] text-white/40 mt-0.5">
                {admin.status === 'active'
                  ? 'Este administrador pode acessar o painel normalmente.'
                  : 'O acesso está suspenso. O administrador não pode entrar no painel.'}
              </p>
            </div>
            <button
              onClick={toggleStatus}
              disabled={saving}
              className={`px-4 py-2 rounded-xl text-[12px] font-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 ${
                admin.status === 'active'
                  ? 'bg-orange-500/15 text-orange-400 hover:bg-orange-500/25'
                  : 'bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25'
              }`}
            >
              <i className={`${admin.status === 'active' ? 'ri-pause-circle-line' : 'ri-play-circle-line'} mr-1.5`} />
              {admin.status === 'active' ? 'Suspender acesso' : 'Reativar acesso'}
            </button>
          </div>

          {/* Remove */}
          <div className="p-4 bg-red-600/5 border border-red-500/15 rounded-xl">
            {!confirmRemove ? (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[13px] text-white font-500">Remover acesso administrativo</p>
                  <p className="text-[11px] text-white/40 mt-0.5">Remove o papel e o acesso ao painel. O usuário mantém a conta como cliente.</p>
                </div>
                <button
                  onClick={() => setConfirmRemove(true)}
                  className="px-4 py-2 bg-red-600/15 text-red-400 hover:bg-red-600/25 text-[12px] font-600 rounded-xl cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-user-unfollow-line mr-1.5" />Remover
                </button>
              </div>
            ) : (
              <div>
                <p className="text-[13px] text-red-300 font-600 mb-1">Confirmar remoção de acesso?</p>
                <p className="text-[11px] text-white/40 mb-3">Esta ação não pode ser desfeita. O usuário perderá o acesso imediatamente.</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setConfirmRemove(false)}
                    className="flex-1 py-2 border border-white/10 text-white/60 text-[12px] font-600 rounded-xl hover:bg-white/5 cursor-pointer whitespace-nowrap transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={removeAdmin}
                    disabled={removing}
                    className="flex-1 py-2 bg-red-600/30 hover:bg-red-600/50 text-red-300 text-[12px] font-600 rounded-xl cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50"
                  >
                    {removing ? 'Removendo...' : 'Sim, remover acesso'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
